import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Crown, Download, Image, LifeBuoy, Lock, MessageSquare, Send, X, XCircle } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { apiDelete, apiGet, apiPost } from '@/services/api-client';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';
import { resizeToMaxSize } from '@/utils/image';

type SupportCaseStatus = 'open' | 'closed';

interface SupportCaseItem {
  id: string;
  subject: string;
  status: SupportCaseStatus;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string;
  lastMessagePreview: string;
  lastMessageBy: 'user' | 'admin';
  userUnreadCount: number;
}

interface SupportMessageItem {
  id: string;
  senderRole: 'user' | 'admin';
  senderName: string;
  text: string;
  imageUrl?: string;
  imageBlobName?: string;
  createdAt: string;
}

interface SupportSummary {
  unreadCount: number;
  monthlyLimit: number;
  createdThisMonth: number;
  remainingThisMonth: number;
  canCreateCase: boolean;
  openCaseId: string | null;
}

function getSupportStatusTagClass(status: SupportCaseStatus): string {
  return status === 'open'
    ? 'px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap border border-emerald-500/20 bg-emerald-500/10 text-emerald-700'
    : 'px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap border border-slate-400/20 bg-slate-500/10 text-slate-600';
}

export default function SupportPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const selectedCaseSectionRef = useRef<HTMLElement | null>(null);
  const messagesListRef = useRef<HTMLDivElement | null>(null);
  const newCaseImageInputRef = useRef<HTMLInputElement | null>(null);
  const replyImageInputRef = useRef<HTMLInputElement | null>(null);
  const hasAutoScrolledUnreadCaseRef = useRef(false);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [newCaseMessage, setNewCaseMessage] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [newCaseImageFile, setNewCaseImageFile] = useState<File | null>(null);
  const [newCaseImagePreview, setNewCaseImagePreview] = useState<string | null>(null);
  const [newCaseImageBase64, setNewCaseImageBase64] = useState<string | null>(null);
  const [replyImagePreview, setReplyImagePreview] = useState<string | null>(null);
  const [replyImageBase64, setReplyImageBase64] = useState<string | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [caseError, setCaseError] = useState<string | null>(null);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [isCreatingCase, setIsCreatingCase] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [isClosingCase, setIsClosingCase] = useState(false);

  const hasPremium = user?.subscriptionTier === 'premium';

  const casesQuery = useQuery<{ cases: SupportCaseItem[]; summary: SupportSummary }>({
    queryKey: ['support-cases-page', user?.id],
    queryFn: async () => {
      const res = await apiGet<{ cases: SupportCaseItem[]; summary: SupportSummary }>('/support/cases');
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Kunde inte hämta supportärenden');
      }
      return res.data;
    },
    refetchInterval: 30 * 1000,
    enabled: !!user,
  });

  const selectedCase = useMemo(() => {
    return (casesQuery.data?.cases || []).find((item) => item.id === selectedCaseId) || null;
  }, [casesQuery.data?.cases, selectedCaseId]);

  const summary = casesQuery.data?.summary;

  const clearNewCaseImage = () => {
    setNewCaseImageFile(null);
    setNewCaseImagePreview(null);
    setNewCaseImageBase64(null);
    if (newCaseImageInputRef.current) newCaseImageInputRef.current.value = '';
  };

  const clearReplyImage = () => {
    setReplyImagePreview(null);
    setReplyImageBase64(null);
    if (replyImageInputRef.current) replyImageInputRef.current.value = '';
  };

  const handleSelectImage = async (
    file: File | undefined,
    onSuccess: (payload: { file: File; preview: string; base64: string }) => void,
    onError: (message: string | null) => void,
  ) => {
    if (!file) return;
    try {
      const base64 = await resizeToMaxSize(file, 2 * 1024 * 1024, 800);
      onSuccess({ file, preview: `data:image/jpeg;base64,${base64}`, base64 });
      onError(null);
    } catch {
      onError('Kunde inte läsa bilden. Försök med en annan fil.');
    }
  };

  const uploadReplyImage = async (caseId: string, imageBase64: string) => {
    const uploadRes = await apiPost<{ url: string; blobName: string }>(`/support/cases/${caseId}/images`, {
      contentType: 'image/jpeg',
      data: imageBase64,
    });
    if (!uploadRes.success || !uploadRes.data?.url || !uploadRes.data?.blobName) {
      throw new Error(uploadRes.error || 'Kunde inte ladda upp bilden.');
    }
    return uploadRes.data;
  };

  const scrollMessagesToBottom = () => {
    const node = messagesListRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  };

  useEffect(() => {
    const cases = casesQuery.data?.cases || [];
    if (cases.length === 0) {
      setSelectedCaseId(null);
      return;
    }
    if (selectedCaseId && cases.some((item) => item.id === selectedCaseId)) {
      return;
    }
    const unreadCase = cases.find((item) => item.userUnreadCount > 0);
    const openCase = cases.find((item) => item.status === 'open');
    setSelectedCaseId(unreadCase?.id || openCase?.id || cases[0].id);
  }, [casesQuery.data?.cases, selectedCaseId]);

  const messagesQuery = useQuery<{ case: SupportCaseItem; messages: SupportMessageItem[] }>({
    queryKey: ['support-case-messages', selectedCaseId],
    queryFn: async () => {
      const res = await apiGet<{ case: SupportCaseItem; messages: SupportMessageItem[] }>(`/support/cases/${selectedCaseId}/messages`);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Kunde inte hämta meddelanden');
      }
      return res.data;
    },
    refetchInterval: selectedCaseId ? 10 * 1000 : false,
    enabled: !!selectedCaseId,
  });

  useEffect(() => {
    if (!selectedCaseId || !selectedCase || selectedCase.userUnreadCount <= 0) return;
    void apiPost(`/support/cases/${selectedCaseId}/read`, {});
    void queryClient.invalidateQueries({ queryKey: ['support-cases-page', user?.id] });
  }, [selectedCaseId, selectedCase, queryClient, user?.id]);

  useEffect(() => {
    if (!summary?.unreadCount) {
      hasAutoScrolledUnreadCaseRef.current = false;
      return;
    }

    if (!selectedCaseId || hasAutoScrolledUnreadCaseRef.current) {
      return;
    }

    const scrollTimer = window.setTimeout(() => {
      selectedCaseSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 80);

    hasAutoScrolledUnreadCaseRef.current = true;
    return () => window.clearTimeout(scrollTimer);
  }, [selectedCaseId, summary?.unreadCount]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      scrollMessagesToBottom();
    }, 60);
    return () => window.clearTimeout(timer);
  }, [selectedCaseId, messagesQuery.data?.messages?.length]);

  const createCase = async (event: FormEvent) => {
    event.preventDefault();
    setCaseError(null);
    if (!hasPremium) {
      setShowPremiumModal(true);
      return;
    }

    const trimmedSubject = subject.trim().slice(0, 120);
    const trimmedMessage = newCaseMessage.trim().slice(0, 500);
    const hasImage = !!newCaseImageBase64;
    if (!trimmedSubject || trimmedSubject.length < 3) {
      setCaseError('Ange ett ämne på minst 3 tecken.');
      return;
    }
    if (!trimmedMessage && !hasImage) {
      setCaseError('Skriv ett meddelande eller bifoga en bild för att öppna ärendet.');
      return;
    }

    setIsCreatingCase(true);
    const res = await apiPost<{ case: SupportCaseItem }>('/support/cases', {
      subject: trimmedSubject,
      message: trimmedMessage,
      imageData: newCaseImageBase64 || undefined,
      imageContentType: newCaseImageFile?.type || 'image/jpeg',
    });
    setIsCreatingCase(false);

    if (!res.success || !res.data?.case) {
      setCaseError(res.error || 'Kunde inte skapa supportärendet.');
      return;
    }

    setSubject('');
    setNewCaseMessage('');
    clearNewCaseImage();
    await queryClient.invalidateQueries({ queryKey: ['support-cases-page', user?.id] });
    setSelectedCaseId(res.data.case.id);
  };

  const sendReply = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedCaseId || !selectedCase) return;
    const trimmedMessage = replyMessage.trim().slice(0, 500);
    const hasImage = !!replyImageBase64;
    if (!trimmedMessage && !hasImage) {
      setReplyError('Skriv ett meddelande eller bifoga en bild först.');
      return;
    }

    setReplyError(null);
    setIsSendingReply(true);
    let uploadedImage: { url: string; blobName: string } | null = null;
    if (replyImageBase64) {
      try {
        uploadedImage = await uploadReplyImage(selectedCaseId, replyImageBase64);
      } catch (error) {
        setIsSendingReply(false);
        setReplyError(error instanceof Error ? error.message : 'Kunde inte ladda upp bilden.');
        return;
      }
    }

    const res = await apiPost(`/support/cases/${selectedCaseId}/messages`, {
      message: trimmedMessage,
      imageUrl: uploadedImage?.url,
      imageBlobName: uploadedImage?.blobName,
    });
    setIsSendingReply(false);
    if (!res.success) {
      if (uploadedImage?.blobName) {
        void apiDelete(`/support/cases/${selectedCaseId}/images/${encodeURIComponent(uploadedImage.blobName)}`);
      }
      setReplyError(res.error || 'Kunde inte skicka meddelandet.');
      return;
    }

    setReplyMessage('');
    clearReplyImage();
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['support-cases-page', user?.id] }),
      queryClient.invalidateQueries({ queryKey: ['support-case-messages', selectedCaseId] }),
    ]);
  };

  const closeCase = async () => {
    if (!selectedCaseId) return;
    setIsClosingCase(true);
    const res = await apiPost(`/support/cases/${selectedCaseId}/close`, {});
    setIsClosingCase(false);
    if (!res.success) {
      setReplyError(res.error || 'Kunde inte stänga ärendet.');
      return;
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['support-cases-page', user?.id] }),
      queryClient.invalidateQueries({ queryKey: ['support-case-messages', selectedCaseId] }),
    ]);
  };

  const canCreateCase = !!summary?.canCreateCase;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 md:px-8">
      <PremiumRequiredModal
        open={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature="Att öppna ett supportärende"
      />

      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('support.title', 'Support')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('support.subtitle', 'Skapa och följ dina supportärenden direkt i appen.')}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card px-4 py-3 text-sm shadow-sm">
          <div className="font-medium text-foreground">{t('support.monthlyLimitTitle', 'Supportärenden denna månad')}</div>
          <div className="mt-1 text-muted-foreground">{summary?.createdThisMonth || 0} / {summary?.monthlyLimit || 3} · {t('support.remaining', 'Kvar')}: {summary?.remainingThisMonth || 0}</div>
        </div>
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <MessageSquare className="h-4 w-4 text-primary" />
            {t('support.unreadMessages', 'Nya meddelanden')}
          </div>
          <div className="mt-2 text-3xl font-bold">{summary?.unreadCount || 0}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            {hasPremium ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Lock className="h-4 w-4 text-amber-600" />}
            {t('support.accessTitle', 'Åtkomst')}
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            {t('support.premiumAccessPrefix', 'Premium-användare kan öppna upp till 3 ärenden per månad, ett i taget. ')}
            {t('support.freeAccessPrefix', 'Gratis-användare kan maila frågor om sitt konto till ')}
            <a
              href="mailto:support@beredskapshubben.se"
              className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
            >
              support@beredskapshubben.se
            </a>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Crown className="h-4 w-4 text-amber-500" />
            {t('support.statusTitle', 'Status')}
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            {summary?.openCaseId
              ? t('support.openCaseExists', 'Du har redan ett öppet supportärende.')
              : canCreateCase
                ? t('support.canCreate', 'Du kan öppna ett nytt supportärende nu.')
                : t('support.cannotCreate', 'Du kan inte öppna ett nytt supportärende just nu.')}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="space-y-4">
          {!summary?.openCaseId && (
            <section className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold">{t('support.openNew', 'Öppna nytt ärende')}</h2>
                {!hasPremium && <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">Premium</span>}
              </div>
              <form className="space-y-3" onSubmit={createCase}>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t('support.subject', 'Ämne')}</label>
                  <input
                    value={subject}
                    onChange={(event) => setSubject(event.target.value.slice(0, 120))}
                    placeholder={t('support.subjectPlaceholder', 'Kort beskrivning av ditt ärende')}
                    className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t('support.firstMessage', 'Första meddelandet')}</label>
                  <textarea
                    value={newCaseMessage}
                    onChange={(event) => setNewCaseMessage(event.target.value.slice(0, 500))}
                    rows={4}
                    placeholder={t('support.messagePlaceholder', 'Beskriv vad du behöver hjälp med')}
                    className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                  />
                  <div className="mt-1 text-right text-xs text-muted-foreground">{newCaseMessage.trim().length}/500</div>
                </div>
                <div className="space-y-2">
                  <input
                    ref={newCaseImageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                      void handleSelectImage(event.target.files?.[0], ({ file, preview, base64 }) => {
                        setNewCaseImageFile(file);
                        setNewCaseImagePreview(preview);
                        setNewCaseImageBase64(base64);
                      }, setCaseError);
                    }}
                  />
                  {newCaseImagePreview && (
                    <div className="rounded-2xl border border-border bg-muted/20 p-3">
                      <img src={newCaseImagePreview} alt="Förhandsvisning" className="max-h-48 rounded-xl border border-border object-contain" />
                      <button
                        type="button"
                        onClick={clearNewCaseImage}
                        className="mt-3 inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm text-muted-foreground transition hover:bg-muted"
                      >
                        <X className="h-4 w-4" />
                        Ta bort bild
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => newCaseImageInputRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
                  >
                    <Image className="h-4 w-4" />
                    {newCaseImagePreview ? 'Byt bild' : 'Bifoga bild'}
                  </button>
                </div>
                {caseError && <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{caseError}</div>}
                {!hasPremium && <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{t('support.premiumHint', 'Du behöver Premium för att öppna ett supportärende.')}</div>}
                {hasPremium && !canCreateCase && <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{summary?.openCaseId ? t('support.limitOpenCase', 'Du kan bara ha ett öppet supportärende åt gången.') : t('support.limitMonthReached', 'Du har använt alla 3 supportärenden för den här månaden.')}</div>}
                <button
                  type="submit"
                  disabled={isCreatingCase || (hasPremium && !canCreateCase)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <LifeBuoy className="h-4 w-4" />
                  {isCreatingCase ? t('support.creating', 'Skapar ärende...') : t('support.createCase', 'Öppna supportärende')}
                </button>
              </form>
            </section>
          )}

          <section className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold">{t('support.yourCases', 'Dina ärenden')}</h2>
            <div className="space-y-2">
              {(casesQuery.data?.cases || []).length === 0 && <div className="rounded-xl border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">{t('support.noCases', 'Du har inga supportärenden ännu.')}</div>}
              {(casesQuery.data?.cases || []).map((item) => {
                const isSelected = item.id === selectedCaseId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedCaseId(item.id)}
                    className={`w-full rounded-xl border px-3 py-3 text-left transition ${isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 hover:bg-muted/40'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium text-foreground">{item.subject}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{new Date(item.updatedAt).toLocaleString('sv-SE')}</div>
                      </div>
                      <span className={getSupportStatusTagClass(item.status)}>{item.status === 'open' ? t('support.open', 'Öppet') : t('support.closed', 'Stängt')}</span>
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground line-clamp-2">{item.lastMessagePreview}</div>
                    {item.userUnreadCount > 0 && <div className="mt-2 inline-flex items-center rounded-full bg-[#945123] px-2 py-1 text-[11px] font-medium text-white">{item.userUnreadCount} {t('support.newMessages', 'nya meddelanden')}</div>}
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <section ref={selectedCaseSectionRef} className="rounded-2xl border border-border bg-card shadow-sm">
          {!selectedCaseId || !selectedCase ? (
            <div className="flex min-h-[520px] flex-col items-center justify-center px-6 text-center">
              <MessageSquare className="h-10 w-10 text-muted-foreground" />
              <h2 className="mt-4 text-xl font-semibold">{t('support.selectCaseTitle', 'Välj ett ärende')}</h2>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">{t('support.selectCaseDescription', 'När du väljer ett supportärende visas hela konversationen här.')}</p>
            </div>
          ) : (
            <div className="flex min-h-[520px] flex-col">
              <div className="border-b border-border px-5 py-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold">{selectedCase.subject}</h2>
                      <span className={getSupportStatusTagClass(selectedCase.status)}>{selectedCase.status === 'open' ? t('support.open', 'Öppet') : t('support.closed', 'Stängt')}</span>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">{t('support.caseId', 'Ärende-ID')}: {selectedCase.id}</div>
                  </div>
                  {selectedCase.status === 'open' && (
                    <button
                      type="button"
                      onClick={closeCase}
                      disabled={isClosingCase}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:opacity-50"
                    >
                      <XCircle className="h-4 w-4" />
                      {isClosingCase ? t('support.closing', 'Stänger...') : t('support.closeCase', 'Stäng ärende')}
                    </button>
                  )}
                </div>
              </div>

              <div ref={messagesListRef} className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
                {messagesQuery.isLoading && <div className="text-sm text-muted-foreground">{t('support.loadingMessages', 'Laddar meddelanden...')}</div>}
                {(messagesQuery.data?.messages || []).map((message) => (
                  <div key={message.id} className={`max-w-[85%] rounded-2xl px-4 py-3 ${message.senderRole === 'user' ? 'ml-auto bg-slate-800 text-white' : 'bg-[#8a4b14] text-white'}`}>
                    <div className={`text-xs font-semibold ${message.senderRole === 'user' ? 'text-white/85' : 'text-white/85'}`}>{message.senderRole === 'user' ? t('support.you', 'Du') : message.senderName}</div>
                    {message.text && <div className="mt-1 whitespace-pre-wrap text-sm">{message.text}</div>}
                    {message.imageUrl && (
                      <button
                        type="button"
                        onClick={() => setFullscreenImage(message.imageUrl || null)}
                        className="mt-3 block overflow-hidden rounded-xl border border-white/15"
                      >
                        <img src={message.imageUrl} alt="Bilaga" className="max-h-72 w-full object-cover" />
                      </button>
                    )}
                    <div className={`mt-2 text-[11px] ${message.senderRole === 'user' ? 'text-white/70' : 'text-white/70'}`}>{new Date(message.createdAt).toLocaleString('sv-SE')}</div>
                  </div>
                ))}
              </div>

              <div className="border-t border-border px-5 py-4">
                {selectedCase.status === 'closed' ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">{t('support.caseClosedMessage', 'Det här ärendet är stängt. Om du behöver mer hjälp kan du öppna ett nytt ärende när du har en ledig plats kvar den här månaden.')}</div>
                ) : (
                  <form onSubmit={sendReply} className="space-y-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-foreground">{t('support.reply', 'Svara')}</label>
                      <textarea
                        value={replyMessage}
                        onChange={(event) => setReplyMessage(event.target.value.slice(0, 500))}
                        rows={4}
                        placeholder={t('support.replyPlaceholder', 'Skriv ditt svar här')}
                        className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                      />
                      <div className="mt-1 text-right text-xs text-muted-foreground">{replyMessage.trim().length}/500</div>
                    </div>
                    <div className="space-y-2">
                      <input
                        ref={replyImageInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(event) => {
                          void handleSelectImage(event.target.files?.[0], ({ preview, base64 }) => {
                            setReplyImagePreview(preview);
                            setReplyImageBase64(base64);
                          }, setReplyError);
                        }}
                      />
                      {replyImagePreview && (
                        <div className="rounded-2xl border border-border bg-muted/20 p-3">
                          <img src={replyImagePreview} alt="Förhandsvisning" className="max-h-48 rounded-xl border border-border object-contain" />
                          <button
                            type="button"
                            onClick={clearReplyImage}
                            className="mt-3 inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm text-muted-foreground transition hover:bg-muted"
                          >
                            <X className="h-4 w-4" />
                            Ta bort bild
                          </button>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => replyImageInputRef.current?.click()}
                        className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
                      >
                        <Image className="h-4 w-4" />
                        {replyImagePreview ? 'Byt bild' : 'Bifoga bild'}
                      </button>
                    </div>
                    {replyError && <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"><AlertCircle className="mr-2 inline h-4 w-4" />{replyError}</div>}
                    <button
                      type="submit"
                      disabled={isSendingReply}
                      className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Send className="h-4 w-4" />
                      {isSendingReply ? t('support.sending', 'Skickar...') : t('support.send', 'Skicka svar')}
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      {fullscreenImage && (
        <div className="fixed inset-0 z-[10040] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm" onClick={() => setFullscreenImage(null)}>
          <div className="absolute right-4 top-4 flex items-center gap-2">
            <a
              href={fullscreenImage}
              download
              onClick={(event) => event.stopPropagation()}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70"
              aria-label="Ladda ner bild"
            >
              <Download className="h-5 w-5" />
            </a>
            <button
              type="button"
              onClick={() => setFullscreenImage(null)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70"
              aria-label="Stäng bild"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <img
            src={fullscreenImage}
            alt="Förstorad bild"
            className="max-h-full max-w-full rounded-2xl object-contain"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}